import type { AudioAsset, AudioStatus } from '../../types/content'
import generatedStatusData from './audio-generation-status.json'
import { mappings, mappingSeedById } from './concepts'
import { letterNames } from './graphemes'
import { phonemeById, phonemes } from './phonemes'
import { irregularWords, sentences } from './sentences'
import { words } from './words'

export const instructionAudio = [
  ['instruction_listen_zh', '听一听', 'listen'],
  ['instruction_find_zh', '找一找', 'find'],
  ['instruction_choose_zh', '选一选', 'choose'],
  ['instruction_repeat_zh', '跟我读', 'repeat'],
  ['instruction_blend_zh', '把声音连起来', 'blend'],
  ['instruction_segment_zh', '把单词拆开', 'segment'],
  ['instruction_your_turn_zh', '你来试试', 'your-turn'],
  ['instruction_listen_again_zh', '再听一次', 'listen-again'],
  ['instruction_try_again_zh', '再试一次', 'try-again'],
  ['instruction_read_zh', '读一读', 'read'],
  ['instruction_great_zh', '真棒', 'great'],
  ['instruction_complete_zh', '完成啦', 'complete'],
] as const

interface GeneratedStatusRecord {
  status: AudioStatus
  sha256?: string
  referenceId?: string
  durationMs?: number
  sampleRate?: number
  channels?: number
  qaNotes?: string
}

export const generatedAudioStatus = generatedStatusData as Record<string, GeneratedStatusRecord>
const sourceStatus = (assetId: string): AudioStatus => generatedAudioStatus[assetId]?.status ?? 'missing'

const phonemeAssets: AudioAsset[] = phonemes.map((phoneme) => ({
  id: phoneme.audioId,
  type: 'phoneme',
  locale: 'en-US',
  target: phoneme.ipa,
  ipa: phoneme.ipa,
  visibleSpelling: phoneme.canonicalExample,
  path: `/audio/phonemes/en-US/${phoneme.id.replace('phoneme_', '')}.wav`,
  status: sourceStatus(phoneme.audioId),
  linkedPhonemeIds: [phoneme.id],
  linkedGraphemeIds: [phoneme.canonicalGraphemeId],
  qaNotes: [generatedAudioStatus[phoneme.audioId]?.qaNotes, phoneme.production.schwaRisk === 'high' ? 'Isolated stop/affricate: reject any audible added schwa.' : phoneme.production.notes].filter(Boolean).join(' '),
}))

const graphemeAssets: AudioAsset[] = mappings.map((mapping) => {
  const seed = mappingSeedById.get(mapping.id)
  const physicalAssetIds = mapping.phonemeIds.map((id) => phonemeById.get(id)?.audioId).filter((id): id is string => Boolean(id))
  const physicalStatuses = physicalAssetIds.map(sourceStatus)
  const status: AudioStatus = physicalStatuses.some((value) => value === 'rejected') ? 'rejected'
    : physicalStatuses.some((value) => value === 'missing') ? 'missing'
      : physicalStatuses.some((value) => value === 'needs_review') ? 'needs_review'
        : physicalStatuses.every((value) => value === 'verified') ? 'verified'
          : 'generated'
  return {
    id: `grapheme_audio_${mapping.id}`,
    type: 'grapheme',
    locale: 'en-US',
    target: mapping.phonemeIds.join(' + '),
    visibleSpelling: seed?.display ?? mapping.graphemeId,
    physicalAssetIds,
    status,
    linkedPhonemeIds: mapping.phonemeIds,
    linkedGraphemeIds: [mapping.graphemeId],
    qaNotes: mapping.instructionalTarget ? `Instructional target ${mapping.instructionalTarget}; verify composition and context.` : mapping.context,
  }
})

const letterNameAssets: AudioAsset[] = letterNames.map((letter) => ({
  id: letter.audioId,
  type: 'letter_name',
  locale: 'en-US',
  target: letter.ipa,
  ipa: letter.ipa,
  visibleSpelling: letter.letter.toUpperCase(),
  path: letter.path,
  status: sourceStatus(letter.audioId),
  linkedGraphemeIds: [`grapheme_${letter.letter}`],
  qaNotes: 'Letter name only. Never substitute this asset for a phoneme lesson.',
}))

const wordAssets: AudioAsset[] = [...words.map((word) => ({
  id: word.audioId,
  type: 'word' as const,
  locale: 'en-US' as const,
  target: word.spelling,
  visibleSpelling: word.spelling,
  path: `/audio/words/en-US/${word.spelling}.wav`,
  status: sourceStatus(word.audioId),
  linkedPhonemeIds: word.phonemeIds,
  linkedGraphemeIds: word.graphemeIds,
  qaNotes: word.isPseudoWord ? 'Silly-word decoding prompt; do not present as vocabulary.' : 'Whole-word General American recording required.',
})), ...irregularWords.filter((word) => !words.some((candidate) => candidate.audioId === word.audioId)).map((word) => ({
  id: word.audioId,
  type: 'word' as const,
  locale: 'en-US' as const,
  target: word.spelling,
  visibleSpelling: word.spelling,
  path: `/audio/words/en-US/${word.spelling.toLowerCase()}.wav`,
  status: sourceStatus(word.audioId),
  qaNotes: `Irregular word: ${word.noteZh}`,
}))]

const blendAssets: AudioAsset[] = words.filter((word) => word.blendDemoAudioId).map((word) => ({
  id: word.blendDemoAudioId!,
  type: 'blend',
  locale: 'en-US',
  target: word.spelling,
  visibleSpelling: word.spelling,
  path: `/audio/blend-demos/en-US/${word.spelling}.wav`,
  status: sourceStatus(word.blendDemoAudioId!),
  linkedPhonemeIds: word.phonemeIds,
  linkedGraphemeIds: word.graphemeIds,
  qaNotes: 'Natural continuous blending demo; not concatenated phoneme files.',
}))

const instructions: AudioAsset[] = instructionAudio.map(([id, target, slug]) => ({
  id,
  type: 'instruction',
  locale: 'zh-CN',
  target,
  visibleSpelling: target,
  path: `/audio/instructions/zh-CN/${slug}.wav`,
  status: sourceStatus(id),
  qaNotes: 'Short, warm, natural child-facing Mandarin; no obvious machine prosody.',
}))

const feedback: AudioAsset[] = [
  { id: 'feedback_success', type: 'feedback', locale: 'none', target: 'success', path: '/audio/feedback/success.wav', status: sourceStatus('feedback_success'), qaNotes: 'Gentle and brief; no speech required.' },
  { id: 'feedback_lesson_complete', type: 'feedback', locale: 'none', target: 'lesson complete', path: '/audio/feedback/lesson-complete.wav', status: sourceStatus('feedback_lesson_complete'), qaNotes: 'Warm completion sound.' },
  { id: 'feedback_gentle_prompt', type: 'feedback', locale: 'none', target: 'gentle prompt', path: '/audio/feedback/gentle-prompt.wav', status: sourceStatus('feedback_gentle_prompt'), qaNotes: 'Never use as an error buzzer.' },
]

const sentenceAssets: AudioAsset[] = sentences.map((sentence) => ({
  id: sentence.audioId,
  type: 'sentence',
  locale: 'en-US',
  target: sentence.text,
  visibleSpelling: sentence.text,
  path: `/audio/sentences/en-US/${sentence.id}.wav`,
  status: sourceStatus(sentence.audioId),
  qaNotes: 'Calm General American connected-text model; preserve child-readable pacing.',
}))

export const audioAssets: AudioAsset[] = [
  ...phonemeAssets,
  ...graphemeAssets,
  ...letterNameAssets,
  ...wordAssets,
  ...blendAssets,
  ...instructions,
  ...feedback,
  ...sentenceAssets,
]

export const audioAssetById = new Map(audioAssets.map((asset) => [asset.id, asset]))
